import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./lib/useAuth";
import { AppShellModern } from "./components/AppShellModern";
import { Spinner } from "./components/ui-modern";
import { LoginPageModern } from "./routes/LoginPageModern";
import { DashboardPageModern } from "./routes/DashboardPageModern";
import { CustomersListPageModern } from "./routes/CustomersListPageModern";
import { NewCustomerPageModern } from "./routes/NewCustomerPageModern";
import { EditCustomerPageModern } from "./routes/EditCustomerPageModern";
import { CustomerDetailPageModern } from "./routes/CustomerDetailPageModern";
import { NewVehiclePageModern } from "./routes/NewVehiclePageModern";
import { EditVehiclePageModern } from "./routes/EditVehiclePageModern";
import { TechniciansListPageModern } from "./routes/TechniciansListPageModern";
import { NewTechnicianPageModern } from "./routes/NewTechnicianPageModern";
import { TechnicianEditPageModern } from "./routes/TechnicianEditPageModern";
import { WorkOrdersListPageModern } from "./routes/WorkOrdersListPageModern";
import { NewWorkOrderPageModern } from "./routes/NewWorkOrderPageModern";
import { WorkOrderEditorPageModern } from "./routes/WorkOrderEditorPageModern";
import { InvoicesListPageModern } from "./routes/InvoicesListPageModern";
import { InvoiceDetailPageModern } from "./routes/InvoiceDetailPageModern";
import { SettingsPageModern } from "./routes/SettingsPageModern";
import { PredefinedItemsPageModern } from "./routes/PredefinedItemsPageModern";
import { OwnerProfilePageModern } from "./routes/OwnerProfilePageModern";
import { AdminPageModern } from "./routes/AdminPageModern";
import { SearchResultsPageModern } from "./routes/SearchResultsPageModern";
import { PaymentsReceivedReportPageModern } from "./routes/PaymentsReceivedReportPageModern";
import { InspectionsPageModern } from "./routes/InspectionsPageModern";
import { SupportPageModern } from "./routes/SupportPageModern";
import { AdminSupportInquiriesListPageModern } from "./routes/AdminSupportInquiriesListPageModern";
import { AdminSupportInquiryDetailPageModern } from "./routes/AdminSupportInquiryDetailPageModern";

function Protected({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner label="Loading AutoRepair…" />
      </div>
    );
  }
  if (!session) return <Navigate to="/login" replace />;
  return <AppShellModern>{children}</AppShellModern>;
}

export default function App() {
  const { session, loading } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={loading ? null : session ? <Navigate to="/" replace /> : <LoginPageModern />} />
      <Route path="/" element={<Protected><DashboardPageModern /></Protected>} />
      <Route path="/search" element={<Protected><SearchResultsPageModern /></Protected>} />
      <Route path="/customers" element={<Protected><CustomersListPageModern /></Protected>} />
      <Route path="/customers/new" element={<Protected><NewCustomerPageModern /></Protected>} />
      <Route path="/customers/:customerNumber" element={<Protected><CustomerDetailPageModern /></Protected>} />
      <Route path="/customers/:customerNumber/edit" element={<Protected><EditCustomerPageModern /></Protected>} />
      <Route path="/customers/:customerNumber/vehicles/new" element={<Protected><NewVehiclePageModern /></Protected>} />
      <Route path="/customers/:customerNumber/vehicles/:vehicleId/edit" element={<Protected><EditVehiclePageModern /></Protected>} />
      <Route path="/technicians" element={<Protected><TechniciansListPageModern /></Protected>} />
      <Route path="/technicians/new" element={<Protected><NewTechnicianPageModern /></Protected>} />
      <Route path="/technicians/:technicianId/edit" element={<Protected><TechnicianEditPageModern /></Protected>} />
      <Route path="/work-orders" element={<Protected><WorkOrdersListPageModern /></Protected>} />
      <Route path="/work-orders/new" element={<Protected><NewWorkOrderPageModern /></Protected>} />
      <Route path="/work-orders/:workOrderNumber" element={<Protected><WorkOrderEditorPageModern /></Protected>} />
      <Route path="/invoices" element={<Protected><InvoicesListPageModern /></Protected>} />
      <Route path="/invoices/:invoiceNumber" element={<Protected><InvoiceDetailPageModern /></Protected>} />
      <Route path="/settings" element={<Protected><SettingsPageModern /></Protected>} />
      <Route path="/settings/predefined-items" element={<Protected><PredefinedItemsPageModern /></Protected>} />
      <Route path="/reports/payments-received" element={<Protected><PaymentsReceivedReportPageModern /></Protected>} />
      <Route path="/inspections" element={<Protected><InspectionsPageModern /></Protected>} />
      <Route path="/profile" element={<Protected><OwnerProfilePageModern /></Protected>} />
      <Route path="/support" element={<Protected><SupportPageModern /></Protected>} />
      <Route path="/admin" element={<Protected><AdminPageModern /></Protected>} />
      <Route path="/admin/support" element={<Protected><AdminSupportInquiriesListPageModern /></Protected>} />
      <Route path="/admin/support/:id" element={<Protected><AdminSupportInquiryDetailPageModern /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
